import { gapi } from 'gapi-script';
import React, { Component } from 'react';
import { verifyYouTubeSubscription } from '../../parts/api';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'https://helpmediy.org:3001/api';

class YouTubeLogin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loggedIn: false,
      name: '',
      email: '',
      isSubmitting: false,
      error: null,
      success: false,
      subscriberVerified: false,
      entitlementError: '',
      needsSubscription: false,
      youtubeChannelId: '',
      isLoadingChannel: false,
      apiReady: false,
      pendingChannelLoad: false,
    };
  }

  isServiceSpecsFlow = () =>
    new URLSearchParams(window.location.search).get('purpose') === 'service-specs';

  serviceSpecsReturnTo = () => {
    const requested = new URLSearchParams(window.location.search).get('returnTo');
    return requested && requested.startsWith('/') && !requested.startsWith('//')
      ? requested
      : '/parts';
  };

  redirectToServiceSpecs = () => {
    window.location.href = this.serviceSpecsReturnTo();
  };

  authenticate = () => {
    return gapi.auth2
      .getAuthInstance()
      .signIn({
        scope:
          'https://www.googleapis.com/auth/youtube.force-ssl https://www.googleapis.com/auth/youtube.readonly',
      })
      .then(
        () => {
          console.log('Sign In Successful');
          this.setState({ loggedIn: true }, () => {
            if (this.isServiceSpecsFlow()) {
              this.completeServiceSpecsVerification();
            } else if (this.state.apiReady) {
              this.loadYouTubeChannelId();
            } else {
              this.setState({ pendingChannelLoad: true });
            }
          });
        },
        function (err) {
          console.log('Error signing in: ' + JSON.stringify(err));
        }
      );
  };

  switchAccount = () => {
    const authInstance = gapi.auth2.getAuthInstance();
    if (!authInstance) return;

    authInstance.signOut().then(() => {
      authInstance.disconnect();
      this.setState({
        loggedIn: false,
        name: '',
        email: '',
        isSubmitting: false,
        error: null,
        success: false,
        subscriberVerified: false,
        entitlementError: '',
        needsSubscription: false,
        youtubeChannelId: '',
        isLoadingChannel: false,
        apiReady: this.state.apiReady,
        pendingChannelLoad: false,
      });
    });
  };

  subscribe = () => {
    if (this.state.isSubmitting || this.state.success) return;
    const serviceSpecsFlow = this.isServiceSpecsFlow();
    if (serviceSpecsFlow) return this.completeServiceSpecsVerification({ subscribe: true });
    if (!this.state.youtubeChannelId) {
      this.setState({ error: 'Unable to determine your YouTube channel. Please try again.' });
      return;
    }

    this.setState({ isSubmitting: true, error: null });
    const resource = {
      part: 'snippet',
      resource: {
        snippet: {
          resourceId: {
            kind: 'youtube#channel',
            channelId: process.env.REACT_APP_YOUTUBE_CHANNEL_ID,
          },
        },
      },
    };
    const request = gapi.client.youtube.subscriptions.insert(resource);
    request.execute(function (response) {
      const reason = response?.data?.[0]?.reason || 'unknown';
      const subscribed = Boolean(response?.result) ||
        (response?.code === 400 && reason === 'subscriptionDuplicate');
      if (subscribed) {
        this.createRaffleEntry();
        return;
      }

      let message = 'Subscription failed. Please try again later.';
      if (reason === 'subscriptionForbidden') {
        message = 'You cannot subscribe to your own channel. Please use a different account.';
      }
      this.setState({ isSubmitting: false, error: message });
    }.bind(this));
  };

  verifyServiceSpecsEntitlement = async ({ subscribe = false } = {}) => {
    const authInstance = gapi.auth2.getAuthInstance();
    const googleAccessToken = authInstance
      ?.currentUser?.get()
      ?.getAuthResponse(true)
      ?.access_token;
    if (!googleAccessToken) {
      throw new Error('Unable to verify your subscription with the current YouTube sign-in. Please sign in again and retry.');
    }
    return verifyYouTubeSubscription({ googleAccessToken, subscribe });
  };

  completeServiceSpecsVerification = async ({ subscribe = false } = {}) => {
    this.setState({
      isSubmitting: true,
      error: null,
      entitlementError: '',
      needsSubscription: false,
    });
    try {
      await this.verifyServiceSpecsEntitlement({ subscribe });
      this.setState({
        isSubmitting: false,
        success: true,
        subscriberVerified: true,
      });
      setTimeout(this.redirectToServiceSpecs, 500);
    } catch (error) {
      if (error.status === 403 || error.code === 'NOT_SUBSCRIBED') {
        this.setState({
          isSubmitting: false,
          needsSubscription: true,
          error: null,
        });
        return;
      }
      this.setState({
        isSubmitting: false,
        error: error.message || 'Unable to verify your YouTube subscription.',
      });
    }
  };

  createRaffleEntry = async () => {
    const { name, email, youtubeChannelId } = this.state;

    if (!name || !email) {
      this.setState({
        error: 'Please enter your name and email before subscribing.',
      });
      return;
    }

    this.setState({ isSubmitting: true, error: null, entitlementError: '' });

    try {
      const response = await fetch(`${API_BASE}/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          method: 'YouTube',
          youtubeChannelId,
        }),
      });
      if (!response.ok) {
        const message = await response.text();
        if (!message.includes('already registered')) {
          throw new Error(message || 'Failed to create raffle entry.');
        }
      }

      let subscriberVerified = false;
      let entitlementError = '';
      try {
        await this.verifyServiceSpecsEntitlement();
        subscriberVerified = true;
      } catch (verificationError) {
        entitlementError = verificationError.message || 'Subscription verified for the raffle, but Service Specs access could not be linked.';
      }

      this.setState({
        isSubmitting: false,
        success: true,
        subscriberVerified,
        entitlementError,
      });
      const params = new URLSearchParams(window.location.search);
      const requestedReturn = params.get('returnTo');
      const returnTo = requestedReturn && requestedReturn.startsWith('/') && !requestedReturn.startsWith('//')
        ? requestedReturn
        : '/';
      setTimeout(() => {
        window.location.href = returnTo;
      }, subscriberVerified ? 1200 : 2500);
    } catch (err) {
      this.setState({ isSubmitting: false, error: err.message });
    }
  };

  loadYouTubeChannelId = () => {
    this.setState({ isLoadingChannel: true, error: null });
    gapi.client.youtube.channels
      .list({
        part: 'id',
        mine: true,
      })
      .then((response) => {
        console.log('channels.list response:', response.result);
        const items = response.result.items || [];
        if (!items.length) {
          this.setState({
            error:
              'No YouTube channel found for this account. If this is your primary account, make sure a YouTube channel exists and try again.',
            isLoadingChannel: false,
          });
          return;
        }
        this.setState({
          youtubeChannelId: items[0].id,
          error: null,
          isLoadingChannel: false,
        });
      })
      .catch((err) => {
        console.log('Error loading YouTube channel ID:', err);
        this.setState({
          error:
            'Unable to load your YouTube channel. Please refresh and try again.',
          isLoadingChannel: false,
        });
      });
  };

  componentDidMount() {
    // this is taken directly from Google documentation:
    // https://developers.google.com/api-client-library/javascript/start/start-js
    const self = this;
    const start = () => {
      // 2. Initialize the JavaScript client library.
      gapi.client
        .init({
          client_id: process.env.REACT_APP_YOUTUBE_CLIENT_ID,
          apiKey: process.env.REACT_APP_YOUTUBE_API_KEY,
          scope:
            'https://www.googleapis.com/auth/youtube.force-ssl https://www.googleapis.com/auth/youtube.readonly',
        })
        .then(() => {
          // 3. Initialize and make the API request.
          return gapi.client.request({
            path: 'https://www.googleapis.com/discovery/v1/apis/youtube/v3/rest',
          });
        })
        .then(
          (response) => {
            console.log(response.result);
            gapi.client.load('youtube', 'v3', () => {
              if (!self || !self.setState) return;
              self.setState({ apiReady: true }, () => {
                if (self.state.pendingChannelLoad) {
                  self.setState({ pendingChannelLoad: false });
                  self.loadYouTubeChannelId();
                }
              });
            });
          },
          (reason) => {
            console.log('Error: ' + reason.result.error.message);
          }
        );
    };
    // 1. Load the JavaScript client library.
    gapi.load('client', start);
  }

  render() {
    const {
      name,
      email,
      isSubmitting,
      error,
      success,
      subscriberVerified,
      entitlementError,
      needsSubscription,
      youtubeChannelId,
      isLoadingChannel,
      apiReady,
    } = this.state;

    if (this.isServiceSpecsFlow()) {
      return this.state.loggedIn ? (
        <div className="youTubeLogin">
          <h2>Unlock Service Specs</h2>
          {isSubmitting && <p>Verifying your Help Me DIY YouTube subscription...</p>}
          {success && subscriberVerified && <p>Subscription verified. Returning to your parts diagram...</p>}
          {error && <div>{error}</div>}
          {needsSubscription && (
            <>
              <p>This YouTube channel is not subscribed to Help Me DIY yet.</p>
              <button
                onClick={this.subscribe}
                className="btn btn-danger btn-block"
                disabled={isSubmitting || !apiReady}
              >
                {apiReady ? 'Subscribe & unlock Service Specs' : 'Preparing YouTube...'}
              </button>
            </>
          )}
          {!isSubmitting && !success && (
            <button
              onClick={this.switchAccount}
              className="btn btn-secondary btn-block"
            >
              Use a different YouTube account
            </button>
          )}
        </div>
      ) : (
        <div className="youTubeLogin">
          <h2>Verify your YouTube subscription</h2>
          <p>Sign in to YouTube to verify your Help Me DIY subscription and unlock Service Specs.</p>
          <br></br>
          <button
            onClick={this.authenticate}
            className="btn btn-danger btn-block"
          >
            Sign into YouTube
          </button>
        </div>
      );
    }

    return this.state.loggedIn ? (
      <div className="youTubeLogin">
        <h2>Subscribe to Help Me DIY!</h2>
        <p>Subscribe to enter the current raffle giveaway.</p>
        <br></br>
        {error && <div>{error}</div>}
        {success && subscriberVerified && <div>Success! Your raffle entry and Service Specs access are verified.</div>}
        {success && !subscriberVerified && <div>Success! Your raffle entry has been created.</div>}
        {entitlementError && <div>{entitlementError}</div>}
        {!youtubeChannelId && isLoadingChannel && (
          <div>Loading your YouTube channel...</div>
        )}
        {error &&
          error.includes('No YouTube channel found') && (
            <div>
              <a
                href="https://www.youtube.com/create_channel"
                target="_blank"
                rel="noreferrer"
              >
                Create a YouTube channel
              </a>
            </div>
          )}
        <label>Name:</label>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => this.setState({ name: e.target.value })}
        />
        <label>Email:</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => this.setState({ email: e.target.value })}
        />
        <br></br>
        <button
          onClick={() => {
            this.subscribe();
          }}
          className="btn btn-danger btn-block"
          disabled={isSubmitting || success || !youtubeChannelId}
        >
          {isSubmitting ? 'Submitting...' : 'Subscribe'}
        </button>
        {!youtubeChannelId && (
          <button
            onClick={this.switchAccount}
            className="btn btn-secondary btn-block"
            disabled={isSubmitting}
          >
            Use a different account
          </button>
        )}
      </div>
    ) : (
      <div className="youTubeLogin">
        <h2>Please Authenticate to YouTube</h2>
        <p>Sign in to your YouTube account to enter the current raffle.</p>
        <br></br>
        <button
          onClick={this.authenticate}
          className="btn btn-danger btn-block"
        >
          Sign into YouTube
        </button>
      </div>
    );
  }
}

export default YouTubeLogin;
